import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import TalkLangPills, { type TalkLang } from "@/pages/home/components/TalkLangPills";

const SUBMIT_ADDR = "https://formspree.io/f/mnppayay";
const WHATSAPP = "59893855338";

type Status = { kind: "ok" | "err"; message: string } | null;

export default function ContactPanel() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<Status>(null);
  const [busy, setBusy] = useState(false);
  const [fallbackHref, setFallbackHref] = useState<string | null>(null);
  const [talkLang, setTalkLang] = useState<TalkLang>(i18n.language === "es" ? "es" : "en");

  const waText = talkLang === "es" ? t("contact.waTextEs") : t("contact.waTextEn");
  const waHref = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(waText)}`;
  const waHint = talkLang === "es" ? t("contact.waHintEs") : t("contact.waHintEn");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (String(data.get("_gotcha") || "").trim()) {
      setStatus({ kind: "ok", message: t("contact.ok") });
      form.reset();
      return;
    }

    const sentLang = talkLang;
    const senderName = String(data.get("name") || "").trim();
    const senderService = String(data.get("service") || "").trim();
    const senderMessage = String(data.get("message") || "").trim().slice(0, 220);

    const buildFallbackHref = () => {
      const lead = sentLang === "es" ? t("contact.waLeadEs") : t("contact.waLeadEn");
      const parts = [lead];
      if (senderName) parts.push(sentLang === "es" ? `Mi nombre es ${senderName}.` : `My name is ${senderName}.`);
      if (senderService) parts.push(sentLang === "es" ? `Servicio: ${senderService}.` : `Service: ${senderService}.`);
      if (senderMessage) parts.push(senderMessage);
      return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(parts.join(" "))}`;
    };

    setBusy(true);
    setStatus(null);
    setFallbackHref(null);

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "_gotcha") return;
      body.append(key, String(value));
    });
    body.set("language", talkLang === "es" ? "Español" : "English");
    body.set("_subject", `New patient inquiry - ${String(data.get("name") || "website form")}`);

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        ok?: boolean;
        message?: string;
        error?: string;
        errors?: Array<{ message?: string; field?: string }>;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.error
        || parsed?.message
        || parsed?.errors?.map((e) => e?.message).filter(Boolean).join(", ")
        || responseText
        || "";
      const isSpamOrFail = !response.ok
        || parsed?.ok === false
        || /spam/i.test(serverMsg);

      if (isSpamOrFail) {
        setStatus({ kind: "err", message: t("contact.err") });
        setFallbackHref(buildFallbackHref());
      } else {
        setStatus({ kind: "ok", message: t("contact.ok") });
        setFallbackHref(null);
        form.reset();
      }
    } catch {
      setStatus({ kind: "err", message: t("contact.err") });
      setFallbackHref(buildFallbackHref());
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" data-side="right" id="contact">
      <img
        className="panel-bg"
        src="https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20male%20home%20nurse%20in%20teal%20scrubs%20standing%20at%20a%20bright%20apartment%20doorway%2C%20short%20dark%20hair%20and%20light%20beard%2C%20holding%20a%20smartphone%20in%20one%20hand%2C%20soft%20morning%20daylight%2C%20pale%20warm%20white%20walls%2C%20welcoming%20and%20professional%20expression%2C%20shallow%20depth%20of%20field%2C%20calm%20airy%20clinical%20aesthetic%2C%20no%20text&width=2400&height=1350&seq=nurse-contact-04&orientation=landscape&nocache=true"
        alt={t("contact.sub")}
        width={2400}
        height={1350}
        loading="eager"
        fetchPriority="low"
        decoding="async"
        data-parallax="0.32"
      />
      <div className="panel-veil" />
      <div className="panel-copy" data-parallax="-0.08">
        <p className="stamp" data-reveal>{t("contact.stamp")}</p>
        <h2 data-reveal dangerouslySetInnerHTML={{ __html: t("contact.h2") }} />
        <p className="sub" data-reveal>{t("contact.sub")}</p>

        <p className="lang-note" data-reveal>
          <i className="ri-translate-2" aria-hidden="true" />
          {t("contact.langNote")}
        </p>

        <form className="form-card" onSubmit={onSubmit}>
          <div className="field min-w-0">
            <label id="cf-talk-label">{t("contact.talkLang")}</label>
            <TalkLangPills value={talkLang} onChange={setTalkLang} ariaLabel={t("contact.talkLang")} />
          </div>

          <div className="field-row">
            <div className="field min-w-0">
              <label htmlFor="cf-name">{t("contact.name")}</label>
              <input id="cf-name" name="name" type="text" required placeholder={t("contact.namePh")} autoComplete="name" />
            </div>
            <div className="field min-w-0">
              <label htmlFor="cf-phone">{t("contact.phone")}</label>
              <input id="cf-phone" name="phone" type="tel" required placeholder={t("contact.phonePh")} autoComplete="tel" />
            </div>
          </div>

          <div className="field-row">
            <div className="field min-w-0">
              <label htmlFor="cf-email">{t("contact.email")}</label>
              <input id="cf-email" name="email" type="email" required placeholder={t("contact.emailPh")} autoComplete="email" />
            </div>
            <div className="field min-w-0">
              <label htmlFor="cf-zone">{t("contact.zone")}</label>
              <input id="cf-zone" name="zone" type="text" placeholder={t("contact.zonePh")} autoComplete="address-level2" />
            </div>
          </div>

          <div className="field min-w-0">
            <label htmlFor="cf-service">{t("contact.service")}</label>
            <select id="cf-service" name="service" defaultValue="" required>
              <option value="" disabled>{t("contact.servicePh")}</option>
              <option value={t("contact.service1")}>{t("contact.service1")}</option>
              <option value={t("contact.service2")}>{t("contact.service2")}</option>
              <option value={t("contact.service3")}>{t("contact.service3")}</option>
              <option value={t("contact.service4")}>{t("contact.service4")}</option>
              <option value={t("contact.service5")}>{t("contact.service5")}</option>
            </select>
          </div>

          <div className="field min-w-0">
            <label htmlFor="cf-message">{t("contact.message")}</label>
            <textarea
              id="cf-message"
              name="message"
              maxLength={500}
              rows={4}
              placeholder={t("contact.messagePh")}
            />
          </div>

          <input
            className="hp-field"
            type="text"
            name="_gotcha"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <button type="submit" className="cta justify-center w-full box-border" disabled={busy}>
            {busy ? t("contact.submitting") : t("contact.submit")}
            <i className="ri-send-plane-line" aria-hidden="true" />
          </button>

          {status && (
            <p className="form-status" data-kind={status.kind} role="status">
              {status.message}
            </p>
          )}

          {status?.kind === "err" && fallbackHref && (
            <div className="form-fallback">
              <p className="form-fallback-title">
                <i className="ri-whatsapp-line" aria-hidden="true" />
                {t("contact.fallbackTitle")}
              </p>
              <p className="form-fallback-text">{t("contact.fallbackText")}</p>
              <a
                className="cta form-fallback-btn"
                href={fallbackHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("contact.fallbackBtn")}
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
          )}
        </form>

        <div className="wa-block" data-reveal>
          <div className="wa-head">
            <i className="ri-whatsapp-line" aria-hidden="true" />
            <span>{t("contact.direct")}</span>
            <a href={`tel:+${WHATSAPP}`}>+598 93 855 338</a>
          </div>
          <div className="wa-row">
            <TalkLangPills value={talkLang} onChange={setTalkLang} ariaLabel={t("contact.talkLang")} />
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="cta">
              {t("contact.waBtn")}
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </a>
          </div>
          <p className="wa-note">{waHint}</p>
        </div>
      </div>
    </section>
  );
}